/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/destructuring-assignment */
import { ActionIcon, Group, MultiSelect, OptionsFilter, Select } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { IconBuildingSkyscraper, IconCheck, IconRefresh } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import useCurrentUser from '../../../hooks/useCurrentUser';
import { Permission } from '../../../models/core/departments.type';
import { EntitySearchResponseType } from '../../../models/core/entities.type';
import entitiesService from '../../../services/core/entities.service';
import theme from '../../../theme';
import { SessionStorageKey } from '../../../utils/constants.utils';
import { transformComboBoxGroupDataFromV5toV7 } from '../../../utils/mantine.utils';
import { validate } from '../../../utils/permission.utils';
import ProfileCardLink from '../ProfileCardLink/ProfileCardLink';

type FormViewProps = {
  label: string;
  description: string | null;
  formItem: any;
  required: boolean;
  idsToBeDisabled: number[];
  disabled: boolean;
  callback: (item: EntitySearchResponseType | EntitySearchResponseType[] | null) => void;
  zIndex: number;
  useCache: boolean;
  allowMultiple: boolean;
  query: 'sinir' | undefined;
};

export default function EntityPicker(props: FormViewProps) {
  const [currentUser] = useCurrentUser();
  const [data, setData] = useState<EntitySearchResponseType[]>([]);
  const [loading, setLoading] = useState(false);
  const [dropdownOpened, { open, close }] = useDisclosure(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const result =
        props.query === 'sinir'
          ? (await entitiesService.search({ unidadeSinir: '*', limit: 1000, offset: 0 })).filter((x) => {
              if (currentUser.executivo) {
                return true;
              }
              if (
                validate(Permission.IntegrationSpecialEntity, currentUser.permissoes) &&
                (!!x.acessoMovimentacoes || !!x.acessoSinir)
              ) {
                return true;
              }
              return !!x.acessoSinir;
            })
          : await entitiesService.list();
      setData(result);

      if (!props.query) {
        sessionStorage.setItem(`${SessionStorageKey.EntitySearch}`, JSON.stringify(result));
      }
    } catch (error: any) {
      showNotification({
        title: `Entidade - Pesquisar`,
        message: error?.isBusinessException ? error.description : 'Não foi possível pesquisar entidades.',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (props.useCache) {
      const cacheResult = JSON.parse(
        sessionStorage.getItem(`${SessionStorageKey.EntitySearch}`) || '[]'
      ) as EntitySearchResponseType[];

      if (cacheResult.length > 0) {
        setData(cacheResult.filter((x) => (props.query === 'sinir' ? x.unidadeSinir : true)));
      } else {
        loadData();
      }
    } else {
      loadData();
    }
  }, []);

  const handleRefresh = () => {
    loadData();
  };

  const options = transformComboBoxGroupDataFromV5toV7(
    data.map((x) => ({
      label: `# ${x.idEntidade} - ${x.nomeFantasia || x.razaoSocial || x.nome || ''}`,
      value: x.idEntidade.toString(),
      disabled: props.idsToBeDisabled.includes(x.idEntidade),
      group: (x.nomeFantasia || x.razaoSocial || x.nome || '-').toUpperCase()[0],
    }))
  ).sort((a, b) => a.group.localeCompare(b.group));

  const optionsFilter: OptionsFilter = ({ options: other, search }) => {
    if (search.trim().length === 0) return other;

    const filteredData = data
      .filter((x) => {
        const value = `${x.idEntidade}|${x.nomeFantasia || ''}|${x.razaoSocial || ''}|${x.cnpj || ''}|${
          x.nome || ''
        }|${x.cpf || ''}`;
        return value.toLowerCase().includes(search.toLowerCase());
      })
      .map((y) => y.idEntidade?.toString());

    return other
      .map((option: any) => {
        const items = option.items.filter((x: any) => filteredData.includes(x.value));
        return items.length > 0 ? { ...option, items } : null;
      })
      .filter(Boolean) as any;
  };

  const renderOption = ({ option, checked }: any) => {
    const row = data.find((x) => x.idEntidade?.toString() === option.value);
    if (!row) return option.label;

    return row.cnpj ? (
      <Group justify="space-between">
        {checked && <IconCheck size={16} stroke={2} color="var(--mantine-color-primary-filled)" />}
        <ProfileCardLink
          id={row.idEntidade.toString()}
          name={row.nomeFantasia || row.razaoSocial || '-'}
          nameSize="sm"
          description={row.nomeFantasia ? row.razaoSocial : row.cnpj}
          descriptionSize="xs"
          linkPrefix="entities"
          showLink={false}
        />
      </Group>
    ) : (
      <Group justify="space-between">
        {checked && <IconCheck size={16} stroke={2} color="var(--mantine-color-primary-filled)" />}
        <ProfileCardLink
          id={row.idEntidade.toString()}
          name={row.nome || '-'}
          nameSize="sm"
          description={row.cpf}
          descriptionSize="xs"
          linkPrefix="entities"
          showLink={false}
        />
      </Group>
    );
  };

  return (
    <Group grow preventGrowOverflow={false} wrap="nowrap">
      {props.allowMultiple ? (
        <MultiSelect
          key={`entity-picker-multi_${props.formItem.value?.join?.(',')}`}
          leftSection={<IconBuildingSkyscraper size={15} />}
          label={props.label}
          description={props.description || null}
          placeholder="Selecione..."
          data={options}
          clearable
          searchable
          filter={optionsFilter}
          disabled={props.disabled || loading}
          renderOption={renderOption}
          required={props.required}
          dropdownOpened={dropdownOpened}
          onDropdownOpen={open}
          onDropdownClose={close}
          value={props.formItem.value?.map((v: number | string) => v.toString()) || []}
          onChange={(values: string[]) => {
            const selectedItems = data.filter((x) => values.includes(x.idEntidade.toString()));
            props.callback(selectedItems);
            open();
          }}
          comboboxProps={{ zIndex: props.zIndex + 1, onClose: close }}
        />
      ) : (
        <Select
          key={`entity-picker-single_${props.formItem.value?.toString()}`}
          leftSection={<IconBuildingSkyscraper size={15} />}
          label={props.label}
          description={props.description || null}
          placeholder="Selecione..."
          data={options}
          clearable
          searchable
          filter={optionsFilter}
          disabled={props.disabled || loading}
          renderOption={renderOption}
          required={props.required}
          value={props.formItem.value?.toString() || ''}
          onOptionSubmit={(value: string) => {
            const selectedItem = data.find((x) => x.idEntidade.toString() === value) || null;
            props.callback(selectedItem);
          }}
          comboboxProps={{ zIndex: props.zIndex + 1 }}
        />
      )}

      <ActionIcon
        style={{ marginLeft: -10, marginTop: props.description ? 40 : 20, maxWidth: 15 }}
        radius="sm"
        color="primary"
        variant="outline"
        onClick={handleRefresh}
        disabled={props.disabled || loading}
        loading={loading}
      >
        <IconRefresh size={15} color={theme?.colors?.primary?.[6]} />
      </ActionIcon>
    </Group>
  );
}
